
/* eslint-disable no-unused-vars */
import { debounce } from 'lodash';
import { normalizeSearchInput } from '@gid/vue-common/utils';

import axios from 'axios';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import {
  CondOperator,
  CreateQueryParams,
  RequestQueryBuilder,
  SConditionAND,
  SFields,
} from '@dataui/crud-request';
import { getModule } from 'vuex-module-decorators';

import DatePicker from '@gid/vue-common/components/DatePicker.vue';
import CrudListPagination from '@gid/vue-common/components/crud-list/CrudListPagination.vue';
import CrudListTable from '@gid/vue-common/components/crud-list/CrudListTable.vue';
import CrudListErrorMessage from '@gid/vue-common/components/crud-list/CrudListErrorMessage.vue';
import {
  AccountingDocument,
  AccountingDocumentStatusEnum,
  AccountingDocumentTargetEnum,
  AccountingDocumentTypeEnum,
  DocumentationTypeEnum,
  Email,
  canStartCancellation,
  canTransitionCancellationStatus,
  statusTransitionForAccountingDocument,
  UserRoleEnum,
  PaymentReminderStatusEnum,
} from '@gid/models';

import {
  AccountingDocumentsActionsEnum,
  AccountingDocumentsModule,
} from '../../store/accounting-documents.module';
import { TranslateResult } from 'vue-i18n';
import { ContactProfileEnum } from '@gid/models/dist/entities/contact-profile-enum';
import WarningBadges from '@/components/accounting/WarningBadges.vue';
import JobSelector from '@/views/accounting/JobSelector.vue';
import AccountSelector from '@/views/accounting/AccountSelector.vue';

interface TargetStatusSpec {
  field: 'status' | 'cancellationStatus';
  to: AccountingDocumentStatusEnum;
}

interface TargetStatusSelection {
  key: string;
  value: TargetStatusSpec | 'separator' | 'retry-pending' | 'send-reminder';
  $isDisabled?: boolean;
  _allowedKey?: string;
}

@Component({
  components: {
    DatePicker,
    CrudListPagination,
    CrudListTable,
    CrudListErrorMessage,
    WarningBadges,
    JobSelector,
    AccountSelector,
  },
})
export default class AccountingDocumentsView extends Vue {
  @Prop() documentId?: string;
  documentStatuses = AccountingDocumentStatusEnum;

  targetStatus: TargetStatusSelection | null = null;
  inProgress: boolean = false;
  paymentLinkCopiedId: string | null = null;
  showMoreFilters: boolean = false;
  // GID-2571: the monthly DATEV export controls are hidden by default (used
  // ~once a month) and revealed by a toggle, like showMoreFilters.
  showDatevExport: boolean = false;
  // GID-2571: month picker (YYYY-MM, empty = previous month) for the manual
  // "Send DATEV export" trigger, plus its in-flight guard.
  datevSendMonth: string = '';
  datevSendInProgress: boolean = false;
  recipientRoles = [
    UserRoleEnum.PARTNER,
    UserRoleEnum.BRAND,
    UserRoleEnum.CUSTOMER,
  ];

  created() {
    if (
      this.store.filters.selectedJobs?.length ||
      this.store.filters.selectedRecipients?.length
    ) {
      this.showMoreFilters = true;
    }
    this.refreshDocumentList();
    this.$watch('showMoreFilters', () => {
      this.refreshDocumentList();
    });
  }

  exportForDatev() {
    const { search } = this.buildSearch();
    const query = {
      page: undefined,
      sort: undefined,
      limit: undefined,
      filter: undefined,
    };
    const qb = RequestQueryBuilder.create(query);
    if (search) {
      qb.search(search);
    }
    const queryString = qb.query();
    const path = `/documents-api/accounting/documents/csv?${queryString}&jwt=${this.store?.store?.getters?.access_token}`;
    window.open(path);
  }

  // GID-2571: trigger the server-side monthly DATEV export email. Empty month
  // exports the previous calendar month; a YYYY-MM value exports that month.
  // Runs the identical path as the automated monthly job.
  async sendDatevExport() {
    const month = this.datevSendMonth || undefined;
    const label =
      month || this.$t('accounting.common.datev-previous-month').toString();
    if (
      !window.confirm(
        this.$t('accounting.common.datev-send-confirm', {
          month: label,
        }).toString(),
      )
    ) {
      return;
    }
    this.datevSendInProgress = true;
    const title = this.$t('accounting.common.send-datev-export').toString();
    try {
      await axios.post('/documents-api/accounting/datev-export/send', {
        month,
      });
      this.$bvToast.toast(
        this.$t('accounting.common.datev-send-success', {
          month: label,
        }).toString(),
        { variant: 'success', title },
      );
    } catch (e) {
      this.$bvToast.toast(
        this.$t('accounting.common.datev-send-error').toString(),
        { variant: 'danger', title },
      );
    } finally {
      this.datevSendInProgress = false;
    }
  }

  canCopyPaymentLink(item: AccountingDocument): boolean {
    return (
      item.type === AccountingDocumentTypeEnum.INVOICE &&
      (item.status === AccountingDocumentStatusEnum.CREATED ||
        item.status === AccountingDocumentStatusEnum.SENT) &&
      item.jobs?.length === 1
    );
  }

  copyPaymentLink(item: AccountingDocument) {
    const jobSfid = item.jobs[0].id;
    const link = `https://payment.getitdone.rocks/payment/stripe/for-job/${jobSfid}?documentId=${item.id}`;
    const textarea = document.createElement('textarea');
    textarea.value = link;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    this.paymentLinkCopiedId = item.id;
    setTimeout(() => {
      this.paymentLinkCopiedId = null;
    }, 2000);
  }

  // Computed
  get store(): AccountingDocumentsModule {
    return getModule(AccountingDocumentsModule, this.$store);
  }
  get filterStatusOptions() {
    return Object.values(AccountingDocumentStatusEnum);
  }
  get filterCancellationStatusOptions() {
    // Cancellation lifecycle reuses AccountingDocumentStatusEnum but excludes
    // values that don't apply to a cancellation document (draft/received).
    return Object.values(AccountingDocumentStatusEnum).filter(
      (s) =>
        s !== AccountingDocumentStatusEnum.DRAFT &&
        s !== AccountingDocumentStatusEnum.RECEIVED,
    );
  }
  get filterDocumentTypes() {
    return Object.values(AccountingDocumentTypeEnum);
  }
  get filterTargetTypeOptions() {
    return Object.values(AccountingDocumentTargetEnum);
  }
  get filterReminderStatuseOptions() {
    return Object.values(PaymentReminderStatusEnum);
  }
  get isFinancePowerUser() {
    return this.$store.getters.contactProfiles?.includes(
      ContactProfileEnum.FINANCE_POWER_USER,
    );
  }
  get hasPaymentReminderBlocker() {
    // Button is disabled only if ALL selected documents have blockers
    return this.store.selectedItems.every((doc) =>
      doc.jobs?.some((job) => job.paymentReminderBlocker === true),
    );
  }
  get targetStatusOptions(): TargetStatusSelection[] {
    const options: TargetStatusSelection[] = [
      {
        key: this.$t('accounting.actions.send') as string,
        value: {
          field: 'status',
          to: AccountingDocumentStatusEnum.SEND_PENDING,
        } as any,
        $isDisabled: false,
      },
      {
        key: this.$t('accounting.actions.cancel-document') as string,
        value: {
          field: 'cancellationStatus',
          to: AccountingDocumentStatusEnum.PENDING,
        } as any,
        $isDisabled: false,
      },
      {
        key: this.$t('accounting.actions.send-cancellation-document') as string,
        value: {
          field: 'cancellationStatus',
          to: AccountingDocumentStatusEnum.SEND_PENDING,
        } as any,
        $isDisabled: false,
      },
      {
        key: this.$t(
          'accounting.status.mark-as-never-to-be-paid-original',
        ) as string,
        value: {
          field: 'status',
          to: AccountingDocumentStatusEnum.WRITTEN_OFF,
        } as any,
        $isDisabled: false,
      },
      {
        key: this.$t(
          'accounting.status.mark-as-never-to-be-paid-cancellation',
        ) as string,
        value: {
          field: 'cancellationStatus',
          to: AccountingDocumentStatusEnum.WRITTEN_OFF,
        } as any,
        $isDisabled: false,
      },
    ];

    const allowedTo: { [k: string]: boolean } = {};
    const profiles = this.$store.state.auth.user?.contact?.profiles ?? [];
    for (const option of options) {
      const spec = option.value as any;
      const key = `${spec.field}:${spec.to}`;
      allowedTo[key] = true;
      for (const doc of this.store.selectedItems) {
        const docLike = {
          status: doc.status,
          cancellationStatus: doc.cancellationStatus ?? null,
        };
        let allowed: boolean;
        if (spec.field === 'cancellationStatus') {
          if (
            spec.to === AccountingDocumentStatusEnum.PENDING &&
            !doc.cancellationStatus
          ) {
            allowed = canStartCancellation(docLike, profiles).allowed;
          } else {
            allowed = canTransitionCancellationStatus(
              docLike,
              spec.to,
              profiles,
            );
          }
        } else {
          allowed = statusTransitionForAccountingDocument.isAllowed(
            doc.status,
            spec.to,
            profiles,
          );
        }
        allowedTo[key] = allowedTo[key] && allowed;
        if (
          spec.field === 'status' &&
          spec.to === AccountingDocumentStatusEnum.SEND_PENDING &&
          doc.hasReclamationInProject &&
          !this.isFinancePowerUser
        ) {
          allowedTo[key] = false;
          break;
        }
      }
      (option as any)._allowedKey = key;
    }
    // A document is retryable while it sits in either *_pending state:
    // `pending` (creation never happened) or `send_pending` (creation done,
    // sending stuck) — the latter is the only way out for cancellation
    // documents whose send failed.
    const retryableStatuses = [
      AccountingDocumentStatusEnum.PENDING,
      AccountingDocumentStatusEnum.SEND_PENDING,
    ];
    let allPending = this.store.selectedItems.every(
      (doc) =>
        retryableStatuses.includes(doc.status) ||
        (doc.cancellationStatus != null &&
          retryableStatuses.includes(doc.cancellationStatus)),
    );
    let allSent = this.store.selectedItems.every(
      (doc) => doc.status === AccountingDocumentStatusEnum.SENT,
    );
    const actionsOptions = options
      .map((x) => ({
        ...x,
        $isDisabled: x._allowedKey ? !allowedTo[x._allowedKey] : true,
      }))
      .concat([
        {
          key: '---',
          value: 'separator',
          $isDisabled: true,
        },
        {
          key: this.$t('accounting.actions.retry-pending') as string,
          value: 'retry-pending',
          $isDisabled: !allPending,
        },
      ]);
    return this.store.selectedItems.every(
      (doc) => doc.type === AccountingDocumentTypeEnum.INVOICE,
    )
      ? actionsOptions.concat({
          key: this.$t('accounting.actions.send-payment-reminder') as string,
          value: 'send-reminder',
          $isDisabled: !allSent || this.hasPaymentReminderBlocker,
        })
      : actionsOptions;
  }
  get columns() {
    return [
      {
        key: 'issueDate',
        sortable: true,
        label: this.$t('accounting.document.issue-date'),
      },
      {
        key: 'number',
        sortable: true,
        label: this.$t('accounting.common.document-number'),
      },
      {
        key: 'cancellationNumber',
        sortable: true,
        label: this.$t('accounting.common.document-cancellation-number'),
      },
      {
        key: 'targetName',
        sortable: true,
        label: this.$t('accounting.common.target-name'),
      },
      {
        key: 'target.creditorNumber',
        sortable: true,
        label: this.$t('accounting.common.creditor-number'),
      },
      {
        key: 'target.debitorNumber',
        sortable: true,
        label: this.$t('accounting.common.debitor-number'),
      },
      {
        key: 'totalAmount',
        sortable: true,
        label: this.$t('accounting.common.total-amount'),
      },
      {
        key: 'type',
        sortable: true,
        label: this.$t('accounting.common.document-type'),
      },
      {
        key: 'dueDate',
        sortable: true,
        label: this.$t('accounting.common.due-date'),
      },
      {
        key: 'status',
        sortable: true,
        label: this.$t('accounting.common.document-status'),
      },
      {
        key: 'cancellationStatus',
        sortable: true,
        label: this.$t('accounting.common.cancellation-status'),
      },
    ];
  }

  get showTotalAmount() {
    return (
      this.store.pagination.totalRows &&
      (this.store.totalAmount || this.store.totalAmount === 0)
    );
  }

  getTargetEmail(item) {
    const {
      emails,
      target: { emailForInvoices },
    } = item;

    const emailRegex =
      /accounting\/invoice\/(first|second|third)_reminder|send_document$/;
    const defaultEmail =
      'auftrag@emailbrief.de <i class="material-icons">local_shipping</i>';

    // Find the latest matching email in one pass
    const lastEmail = emails.reduce((latest, email) => {
      if (emailRegex.test(email.template)) {
        // Check if it's the latest email
        return !latest || new Date(email.timestamp) > new Date(latest.timestamp)
          ? email
          : latest;
      }
      return latest;
    }, null);

    // Return the target email or default message
    return (
      lastEmail?.to?.[0] ||
      this.$t('accounting.document.email-will-sent', {
        email: emailForInvoices || defaultEmail,
      })
    );
  }

  @Watch('store.filters', {
    deep: true,
  })
  searchChange(value) {
    this.throttledRefresh();
  }

  buildSearch() {
    const search: Array<SFields | SConditionAND> = [];
    if (this.documentId) {
      const docToSearch = (docId) => {
        const fieldSearch: Array<SFields | SConditionAND> = [];
        let field = /^\d+$/.test(docId) ? 'id' : 'number';

        fieldSearch.push({
          [field]: {
            $eq: docId,
          },
        });

        if (field === 'number') {
          fieldSearch.push({
            outsideCommunicationNumber: {
              $eq: docId,
            },
          });
        }
        return fieldSearch;
      };

      return {
        search: { $or: this.documentId.split(',').flatMap(docToSearch) },
      };
    }

    if (this.store.filters.dateFrom) {
      search.push({
        issueDate: {
          $gte: this.store.filters.dateFrom,
        },
      });
    }
    if (this.store.filters.dateTo) {
      search.push({
        issueDate: {
          $lte: this.store.filters.dateTo,
        },
      });
    }
    if (this.store.filters.status) {
      search.push({
        status: {
          $eq: this.store.filters.status,
        },
      });
    }
    if (this.store.filters.cancellationStatus) {
      search.push({
        cancellationStatus: {
          $eq: this.store.filters.cancellationStatus,
        },
      });
    }
    if (this.store.filters.targetType) {
      search.push({
        'lineItems.targetType': {
          $eq: this.store.filters.targetType,
        },
      });
    }
    if (this.store.filters.paymentReminderBlocked) {
      search.push({
        'jobs.paymentReminderBlocker': {
          $eq: this.store.filters.paymentReminderBlocked,
        },
      });
    }
    // Advanced filters only apply when the panel is expanded
    if (this.showMoreFilters) {
      if (this.store.filters.dueDateFrom) {
        search.push({
          dueDate: {
            $gte: this.store.filters.dueDateFrom,
          },
        });
      }
      if (this.store.filters.dueDateTo) {
        search.push({
          dueDate: {
            $lte: this.store.filters.dueDateTo,
          },
        });
      }
      if (this.store.filters.cancellationDateFrom) {
        search.push({
          cancellationDate: {
            $gte: this.store.filters.cancellationDateFrom,
          },
        });
      }
      if (this.store.filters.cancellationDateTo) {
        search.push({
          cancellationDate: {
            $lte: this.store.filters.cancellationDateTo,
          },
        });
      }
      if (this.store.filters.reminderStatus) {
        search.push({
          paymentReminderStatus: {
            $eq: this.store.filters.reminderStatus,
          },
        });
      }
    }
    if (this.store.filters.docType) {
      search.push({
        type: {
          $eq: this.store.filters.docType,
        },
      });
    }

    const searchTerm = normalizeSearchInput(this.store.filters.search);
    if (searchTerm) {
      search.push({
        $or: [
          {
            number: {
              [CondOperator.CONTAINS_LOW]: searchTerm,
            },
          },
          {
            'jobs.name': {
              [CondOperator.CONTAINS_LOW]: searchTerm,
            },
          },
          {
            outsideCommunicationNumber: {
              [CondOperator.CONTAINS_LOW]: searchTerm,
            },
          },
          {
            'target.name': {
              [CondOperator.CONTAINS_LOW]: searchTerm,
            },
          },
          {
            cancellationNumber: {
              [CondOperator.CONTAINS_LOW]: searchTerm,
            },
          },
        ],
      });
    }

    if (this.showMoreFilters) {
      if (this.store.filters.selectedJobs?.length) {
        search.push({
          'jobs.id': {
            $in: this.store.filters.selectedJobs.map((j) => j.id),
          },
        });
      }

      if (this.store.filters.selectedRecipients?.length) {
        search.push({
          'target.id': {
            $in: this.store.filters.selectedRecipients.map((r) => r.id),
          },
        });
      }
    }

    return {
      search: {
        $and: search,
      },
    };
  }

  refreshDocumentList() {
    const { search } = this.buildSearch();

    const query: CreateQueryParams = {
      page: this.store.pagination.currentPage,
      limit: this.store.pagination.perPage,
    };
    this.store[AccountingDocumentsActionsEnum.ITEMS_FETCH]({
      query,
      search,
    });
  }
  throttledRefresh = debounce(this.refreshDocumentList, 400);

  hideModal() {
    this.$bvModal.hide('remainder-modal');
  }

  showModal() {
    this.$bvModal.show('remainder-modal');
  }
  modalSendReminder() {
    this.sendReminder();
    this.hideModal();
  }

  setStatus() {
    if (this.targetStatus?.value === 'retry-pending') {
      if (
        confirm(
          'Make sure you know what you are doing!\n If not sure please consult engineering.',
        )
      ) {
        return this.retryPending();
      }
      return;
    }

    if (this.targetStatus?.value === 'send-reminder') {
      if (
        this.store.selectedItems.find((item) =>
          item.emails.find((e) => e.template.indexOf('third_reminder') != -1),
        )
      ) {
        return this.showModal();
      }
      return this.sendReminder();
    }
    const spec = this.targetStatus?.value as TargetStatusSpec | undefined;
    if (
      !spec ||
      typeof spec !== 'object' ||
      !spec.field ||
      !Object.values(AccountingDocumentStatusEnum).includes(spec.to as any)
    ) {
      console.warn('Documents.setStatus Invalid target ', spec);
      return;
    }
    const bulk = this.store.selectedItems.map((fullDoc) => {
      const doc: Partial<AccountingDocument> = new AccountingDocument();
      doc.id = fullDoc.id;
      if (spec.field === 'cancellationStatus') {
        doc.cancellationStatus = spec.to;
      } else {
        doc.status = spec.to;
      }
      return doc;
    });

    this.inProgress = true;
    this.store[AccountingDocumentsActionsEnum.ITEMS_UPDATE_ITEMS]({
      data: bulk,
      disableBulk: true,
      updatedFields: [spec.field],
    })
      .then(() => {
        this.store.selectionSetAll(false);
        return this.refreshDocumentList();
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  async sendReminder() {
    const docs = this.store.selectedItems
      .filter((doc) => doc.status === AccountingDocumentStatusEnum.SENT)
      .filter((doc) => {
        // Filter out documents that have jobs with paymentReminderBlocker
        return !doc.jobs?.some((job) => job.paymentReminderBlocker === true);
      })
      .map((doc) => doc.id);
    this.inProgress = true;
    Promise.all(
      docs.map((id) => {
        return axios.post(`${this.store.crudEndpoint}/reminder`, { id });
      }),
    )
      .then((res) => {
        return this.refreshDocumentList();
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  retryPending() {
    const docs = this.store.selectedItems.map((fullDoc) => {
      const doc: Partial<AccountingDocument> = new AccountingDocument();
      doc.id = fullDoc.id;
      return doc;
    });

    this.inProgress = true;
    axios
      .post(`${this.store.crudEndpoint}/retry-pending`, docs)
      .then(() => {
        this.store.selectionSetAll(false);
        return this.refreshDocumentList();
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  hasPaymentReminderBlockerForDocument(doc: AccountingDocument) {
    return doc.jobs?.some((job) => job.paymentReminderBlocker === true);
  }

  remindersInfo(allEmails: Email[]) {
    const remindersRegEx = /accounting\/invoice\/(first|second|third)_reminder/;
    const lastReminder = [...allEmails]
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
      .find((email) => remindersRegEx.test(email.template));
    if (lastReminder) {
      const name = lastReminder.template.match(remindersRegEx)?.[1];
      const label = this.$t(`reminders.${name}_reminder`);
      const date = new Date(lastReminder.timestamp).toLocaleString();
      return `${label} ${date}`;
    }
  }
}
